import { setChangeBookingFlow } from '@/features/manage/manageActions';
import { TransText } from '@/i18n/trans/text';
import { useDispatch } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import { Button, Icons, Popover, Sheet } from '@packages/webshop-design-system';
import { type FC, type ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';

interface MakeChangesProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  trigger?: ReactNode;
  onChangeTripsClick: () => void;
  onChangeTravelClassClick: () => void;
  onEditPassengerDetailsClick: () => void;
}

const MakeChanges: FC<MakeChangesProps> = ({
  isOpen,
  onOpenChange,
  trigger,
  onChangeTripsClick,
  onChangeTravelClassClick,
  onEditPassengerDetailsClick,
}) => {
  const dispatch = useDispatch();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const handleChangeTrips = () => {
    dispatch(setChangeBookingFlow('changeTrips'));
    onChangeTripsClick();
    onOpenChange(false);
  };

  const handleChangeTravelClass = () => {
    dispatch(setChangeBookingFlow('changeTravelClass'));
    onChangeTravelClassClick();
    onOpenChange(false);
  };

  const handleEditPassengerDetails = () => {
    onEditPassengerDetailsClick();
    onOpenChange(false);
  };

  const menuContent = (
    <div className="flex flex-col gap-2">
      <Button
        variant="secondary"
        onClick={handleChangeTrips}
        className="justify-center whitespace-normal text-center"
        data-testid="make-changes-change-trips"
      >
        <Icons.transfer width={20} height={20} className="shrink-0" />
        <TransText i18nKey="changeTrips" />
      </Button>
      <Button
        variant="secondary"
        onClick={handleChangeTravelClass}
        className="justify-center whitespace-normal text-center"
        data-testid="make-changes-change-travel-class"
      >
        <Icons.ticketManage width={20} height={20} className="shrink-0" />
        <TransText i18nKey="changeTravelClass" />
      </Button>
      <Button
        variant="secondary"
        onClick={handleEditPassengerDetails}
        className="justify-center whitespace-normal text-center"
        data-testid="make-changes-edit-passengers"
      >
        <Icons.edit width={20} height={20} className="shrink-0" />
        <TransText i18nKey="editPassengerDetails" />
      </Button>
    </div>
  );

  if (isLaptopOrBigger) {
    return (
      <Popover
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        trigger={trigger}
        data-testid="make-changes-popover"
        placement="top"
        content={menuContent}
        className={{ root: 'w-max min-w-64' }}
      />
    );
  }

  return (
    <Sheet
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      trigger={trigger}
      side="bottom"
      headerTitle={<TransText i18nKey="makeChanges" />}
      className={{ root: 'min-w-full' }}
      data-testid="make-changes-sheet"
    >
      {menuContent}
    </Sheet>
  );
};

export default MakeChanges;
